<template>
  <div v-if="allowed" class="copy-breaks" data-cy="copy-breaks-to-teams">
    <v-btn
      ref="triggerEl"
      variant="outlined"
      color="primary"
      data-cy="copy-breaks-to-teams-btn"
      :loading="teamsLoading"
      :disabled="actionDisabled"
      :aria-describedby="noTeams ? 'copy-breaks-no-teams' : undefined"
      @click="openDialog"
    >
      Copy breaks to team(s)
    </v-btn>
    <p
      v-if="noTeams"
      id="copy-breaks-no-teams"
      class="copy-breaks-note"
      data-cy="copy-breaks-no-teams"
    >
      This teacher isn't on any team.
    </p>
    <p v-if="loadError" class="copy-breaks-note" role="alert" data-cy="copy-breaks-load-error">
      {{ loadError }}
    </p>

    <dialog
      ref="dialogEl"
      class="copy-breaks-dialog"
      aria-modal="true"
      aria-labelledby="copy-breaks-dialog-title"
      data-cy="copy-breaks-confirm-modal"
      @cancel="onCancel"
      @close="focusTrigger"
      @click="onBackdropClick"
    >
      <div class="copy-breaks-dialog-top">
        <h2 id="copy-breaks-dialog-title" class="copy-breaks-title" tabindex="-1">
          Copy breaks to team(s)
        </h2>
        <button
          type="button"
          class="copy-breaks-icon-btn"
          aria-label="Close"
          data-cy="copy-breaks-dialog-close"
          :disabled="copying"
          @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
        </button>
      </div>

      <div class="copy-breaks-dialog-body">
        <p v-if="!result" data-cy="copy-breaks-confirm-text">
          This is a one-time copy of {{ teacherName }}'s breaks for {{ dayLabel }}. Later changes
          will not sync. It applies to this day only.
          <template v-if="teams.length === 1">
            Breaks will be copied to {{ teams[0].team_name }}.
          </template>
        </p>

        <fieldset
          v-if="!result && teams.length > 1"
          class="team-picker"
          data-cy="copy-breaks-team-picker"
        >
          <legend>Teams</legend>
          <label v-for="team in teams" :key="team.uid" class="team-choice">
            <input
              v-model="selected"
              type="checkbox"
              :value="team.uid"
              :data-cy="`copy-breaks-team-${team.uid}`"
            />
            <span>{{ team.team_name }}</span>
          </label>
        </fieldset>
        <p
          v-if="!result && teams.length > 1 && !selected.length"
          data-cy="copy-breaks-select-team"
          aria-live="polite"
        >
          Select at least one team.
        </p>

        <div
          v-if="result"
          id="copy-breaks-result"
          role="alert"
          tabindex="-1"
          data-cy="copy-breaks-result"
        >
          <p>{{ summary }}</p>
          <div v-if="groupedSkipped.length" data-cy="copy-breaks-skipped">
            <div v-for="group in groupedSkipped" :key="group.team_uid" class="skipped-group">
              <strong>{{ group.team_name }}</strong>
              <ul>
                <li v-for="(item, index) in group.items" :key="`${group.team_uid}-${index}`">
                  {{ item.timeslot }} — {{ item.reason }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p
          v-if="copyError"
          id="copy-breaks-error"
          class="copy-breaks-error"
          role="alert"
          tabindex="-1"
          data-cy="copy-breaks-error"
        >
          {{ copyError }}
        </p>
      </div>

      <div class="copy-breaks-dialog-actions">
        <template v-if="!result">
          <button
            type="button"
            class="copy-breaks-text-btn"
            data-cy="copy-breaks-cancel-btn"
            :disabled="copying"
            @click="closeDialog"
          >
            Cancel
          </button>
          <v-btn
            color="primary"
            data-cy="copy-breaks-confirm-btn"
            :loading="copying"
            :disabled="teams.length > 1 && !selected.length"
            @click="confirmCopy"
          >
            Copy breaks
          </v-btn>
        </template>
        <v-btn v-else color="primary" data-cy="copy-breaks-close-btn" @click="closeDialog">
          Close
        </v-btn>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import FetchService from "@/services/fetch.service";
import { useAuthenticationStore } from "@/stores/authentication";
import { useAgendaStore } from "@/stores/teacher/agenda";
import {
  buildCopyBreaksRequest,
  canCopyBreaksToTeams,
  copyBreaksErrorMessage,
  copyBreaksSummary,
  groupSkippedByTeam,
  isCopyBreaksResult,
  parseTeacherTeams,
  refreshTeamCalendarIfLoaded,
  type CopyBreaksResult,
  type TeacherTeam
} from "./copyBreaksToTeams";

const props = defineProps<{
  teacherUid: string;
  teacherName: string;
  dayLabel: string;
  conferenceDaysIds: string[];
}>();

const authStore = useAuthenticationStore();
const dialogEl = ref<HTMLDialogElement | null>(null);
const triggerEl = ref<{ $el?: HTMLElement } | HTMLElement | null>(null);
const teams = ref<TeacherTeam[]>([]);
const selected = ref<string[]>([]);
const teamsLoading = ref(false);
const copying = ref(false);
const loaded = ref(false);
const loadError = ref("");
const copyError = ref("");
const result = ref<CopyBreaksResult | null>(null);

const allowed = computed(() =>
  canCopyBreaksToTeams({
    level: authStore.level,
    authentication_type: authStore.jwtData?.authentication_type,
    set_breaks: authStore.me?.set_breaks,
    modify_schedule: authStore.me?.modify_schedule
  })
);

const actionDisabled = computed(
  () => teamsLoading.value || !loaded.value || !teams.value.length || !!loadError.value
);

const noTeams = computed(
  () => loaded.value && !teamsLoading.value && !loadError.value && !teams.value.length
);

const summary = computed(() =>
  result.value ? copyBreaksSummary(result.value.copied, result.value.skipped) : ""
);

const groupedSkipped = computed(() => groupSkippedByTeam(result.value?.skipped_details));

const loadTeams = async () => {
  if (!allowed.value || !props.teacherUid) return;
  teamsLoading.value = true;
  loadError.value = "";
  loaded.value = false;
  const res = await FetchService.fetchData(`/teachers/${props.teacherUid}/teams`, "get");
  const parsed = parseTeacherTeams(res);
  teams.value = parsed.teams;
  selected.value = parsed.teams.map((team) => team.uid);
  loadError.value = parsed.error;
  loaded.value = true;
  teamsLoading.value = false;
};

const focusTrigger = () => {
  const target = triggerEl.value as { $el?: HTMLElement } | HTMLElement | null;
  const button = target && "$el" in target ? target.$el : target;
  button?.focus?.();
};

const showDialog = () => {
  dialogEl.value?.showModal();
};

const closeDialog = () => {
  dialogEl.value?.close();
};

const openDialog = async () => {
  if (actionDisabled.value) return;
  selected.value = teams.value.map((team) => team.uid);
  result.value = null;
  copyError.value = "";
  showDialog();
  await nextTick();
  document.getElementById("copy-breaks-dialog-title")?.focus();
};

const onCancel = (event: Event) => {
  if (copying.value) event.preventDefault();
};

const onBackdropClick = (event: MouseEvent) => {
  if (event.target === dialogEl.value && !copying.value) closeDialog();
};

const confirmCopy = async () => {
  if (copying.value) return;
  if (teams.value.length > 1 && !selected.value.length) return;
  copying.value = true;
  copyError.value = "";
  const body = buildCopyBreaksRequest({
    teacherUid: props.teacherUid,
    conferenceDaysIds: props.conferenceDaysIds,
    selectedTeamUids: selected.value,
    allTeamUids: teams.value.map((team) => team.uid)
  });
  const res = await FetchService.fetchData("/schedule/copy-teacher-breaks", "post", body);
  copying.value = false;
  if (isCopyBreaksResult(res)) {
    result.value = res;
    if (res.copied > 0) {
      refreshTeamCalendarIfLoaded(useAgendaStore(), props.conferenceDaysIds);
    }
    await nextTick();
    document.getElementById("copy-breaks-result")?.focus();
    return;
  }
  copyError.value = copyBreaksErrorMessage(res);
  await nextTick();
  document.getElementById("copy-breaks-error")?.focus();
};

watch(
  () => [props.teacherUid, allowed.value] as const,
  () => {
    loadTeams();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.copy-breaks-note {
  margin: 8px 0 0;
}

.copy-breaks-dialog {
  border: none;
  border-radius: 8px;
  padding: 16px 20px 12px;
  max-width: 520px;
  width: calc(100% - 32px);
  color: $gray-700;
}

.copy-breaks-dialog::backdrop {
  background: rgba(0, 0, 0, 0.46);
}

.copy-breaks-dialog-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.copy-breaks-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.copy-breaks-title:focus {
  outline: 2px solid $primary;
  outline-offset: 2px;
}

.copy-breaks-icon-btn {
  border: none;
  background: transparent;
  border-radius: 20px;
  width: 36px;
  height: 36px;
  cursor: pointer;
}

.copy-breaks-icon-btn:focus-visible,
.team-choice input:focus-visible,
.copy-breaks-text-btn:focus-visible {
  outline: 2px solid $primary;
  outline-offset: 2px;
}

.copy-breaks-dialog-body {
  margin-top: 12px;
}

.team-picker {
  border: 0;
  margin: 12px 0 0;
  padding: 0;
}

.team-choice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.skipped-group {
  margin-top: 8px;
}

.skipped-group ul {
  margin: 4px 0 0;
  padding-left: 1.25rem;
}

.copy-breaks-error {
  color: $danger;
  margin-top: 12px;
}

.copy-breaks-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.copy-breaks-text-btn {
  border: 1px solid $gray-300;
  background: transparent;
  border-radius: 4px;
  padding: 6px 16px;
  cursor: pointer;
}
</style>
